import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '../common/Modal';

const ConvertToProposalModal = ({
  isOpen,
  onClose,
  onConfirm,
  researchData,
  users = [],
  selectedUsers = [],
  onUserToggle
}) => {
  if (!researchData) return null;

  const handleConfirm = () => {
    if (selectedUsers.length === 0) {
      alert('Please select at least one user for proposal access');
      return;
    }
    onConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Convert to Proposal"
      widthClassName="max-w-2xl"
      hideFooter={true}
    >
      <div className="space-y-6">
        {/* Summary of what will be created */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">This will create a new proposal with:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>All RFP details and analysis</li>
                <li>Evaluation criteria from the RFP</li>
                <li>Executive summary and recommendations</li>
                <li>Links to original RFP documents</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Proposal Preview */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Proposal Details</h3>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-600">Title:</span>
              <p className="font-medium text-slate-900">{researchData.rfp_title}</p>
            </div>

            {researchData.rfp_number && (
              <div>
                <span className="text-slate-600">RFP Number:</span>
                <p className="font-medium text-slate-900">{researchData.rfp_number}</p>
              </div>
            )}

            {researchData.customer_name && (
              <div>
                <span className="text-slate-600">Customer:</span>
                <p className="font-medium text-slate-900">{researchData.customer_name}</p>
              </div>
            )}

            {researchData.due_date && (
              <div>
                <span className="text-slate-600">Due Date:</span>
                <p className="font-medium text-slate-900">
                  {new Date(researchData.due_date).toLocaleDateString()}
                </p>
              </div>
            )}

            {researchData.estimated_value && (
              <div>
                <span className="text-slate-600">Estimated Value:</span>
                <p className="font-medium text-slate-900">
                  ${(researchData.estimated_value / 1000000).toFixed(1)}M
                </p>
              </div>
            )}

            <div>
              <span className="text-slate-600">Bid Recommendation:</span>
              <p className="font-medium text-slate-900 capitalize">
                {researchData.bid_recommendation?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Evaluation Criteria Preview */}
        {researchData.evaluation_criteria_json && (
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">
              Evaluation Criteria (will be included)
            </h4>
            <p className="text-xs text-slate-600">
              {Object.keys(JSON.parse(researchData.evaluation_criteria_json)).length} criteria factors identified
            </p>
          </div>
        )}

        {/* User Selection */}
        {users.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">
              Grant Proposal Access <span className="text-red-500">*</span>
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {users.map((user) => (
                <label
                  key={user.user_id}
                  className="flex items-center p-2 hover:bg-slate-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.user_id)}
                    onChange={() => onUserToggle(user.user_id)}
                    className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                  </div>
                  {user.role === 'admin' && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Admin
                    </span>
                  )}
                </label>
              ))}
            </div>
            {selectedUsers.length === 0 && (
              <p className="text-sm text-red-600 mt-2">
                Please select at least one user
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={selectedUsers.length === 0}
          >
            <FontAwesomeIcon icon="rocket" />
            <span>Create Proposal</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConvertToProposalModal;
